import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    MenuItem, Select, Stack, TextField, Typography,
} from "@mui/material";
import { formatDateTime } from "../../common/common";
import { tokens } from "../../theme";
import {
    loadCapture, loadCaptureList, loadCaptureScans, setSelectedAuId, uploadCapture,
} from "../../reducer/RawSurfaceSlice";
import RawSurfaceHistoryDialog from "./RawSurfaceHistoryDialog";

/** Matches DataWatcher's toolbar field label - small, uppercase, letter-spaced, muted. */
const fieldLabelSx = {
    fontSize: "10.5px",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: tokens.muted,
    marginBottom: "3px",
};

/** Matches DataWatcher's own copy - the AU/Raw Capture selects' rendered height, so Upload
 *  capture can center against the dropdown itself rather than the label+dropdown column. */
const FIELD_HEIGHT = "58px";

/**
 * A Select's closed-box display always reserves the same two-line height - via an invisible
 * ghost copy of that shape - so the toolbar's height never changes between "nothing selected"
 * and "something selected", and the Alignment Unit select (which has no secondary line of its
 * own) still comes out exactly as tall as the Raw Capture select next to it.
 *
 * A single-line value (no secondary) is centered across that full reserved height rather than
 * pinned to the top - plain flow layout can't do that (the ghost line already fills the height,
 * leaving no free space for a flex/Stack justify-content to distribute), so the real content is
 * absolutely positioned over the ghost and vertically centered there instead. Mirrors
 * DataWatcher.js's own copy.
 */
const twoLineValue = (primary, secondary) => (
    <Box sx={{ position: "relative" }}>
        <Box sx={{ visibility: "hidden" }} aria-hidden="true">
            <Box>{primary}</Box>
            <Box><Typography noWrap sx={{ fontSize: "12px" }}>&nbsp;</Typography></Box>
        </Box>
        {secondary ? (
            <Box sx={{ position: "absolute", inset: 0 }}>
                <Box>{primary}</Box>
                <Box sx={{ color: tokens.muted }}>
                    <Typography noWrap sx={{ fontSize: "12px" }}>{secondary}</Typography>
                </Box>
            </Box>
        ) : (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center" }}>
                {primary}
            </Box>
        )}
    </Box>
);

/**
 * Horizontal toolbar for Raw Surface Analysis: pick an Alignment Unit, pick a previously
 * uploaded capture for it, or upload a new one.
 *
 * Mirrors DataWatcher's Scan Analysis toolbar layout so the two tabs read as one system, but the
 * two are separate components rather than a shared one - DataWatcher also owns scan deletion and
 * the AU list fetch (reused here via the scans slice), which don't apply on this tab.
 */
export default function RawSurfaceToolbar() {
    const scans = useSelector((state) => state.scans);
    const rawSurface = useSelector((state) => state.rawSurface);
    const dispatch = useDispatch();
    const fileInput = useRef(null);

    const [uploadOpen, setUploadOpen] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [description, setDescription] = useState("");
    const [uploadError, setUploadError] = useState(null);
    const [historyOpen, setHistoryOpen] = useState(false);

    const busy = rawSurface.status === "uploading" || rawSurface.status === "fitting";

    // The AU list itself is fetched by DataWatcher, which stays mounted on both tabs (see
    // VisualizerContent's comment on why) - no need to duplicate that fetch here.
    useEffect(() => {
        if (rawSurface.selectedAuId) {
            dispatch(loadCaptureList({ auid: rawSurface.selectedAuId }));
        }
    }, [rawSurface.selectedAuId, dispatch]);

    // Loaded here rather than in RawSurfaceSaveButton: "Compare Saved Scans" lives in this
    // toolbar now, and its enabled state depends on the answer, so it has to be known before the
    // history dialog is ever opened.
    useEffect(() => {
        if (rawSurface.captureId) {
            dispatch(loadCaptureScans({ captureId: rawSurface.captureId }));
        }
    }, [rawSurface.captureId, dispatch]);

    const handleSelectAu = (evt) => {
        dispatch(setSelectedAuId({ auid: evt.target.value }));
    };

    const captureFieldText = () => {
        if (rawSurface.capturesStatus === "loading") return "Loading captures…";
        if (rawSurface.selectedAuId && rawSurface.captures.length === 0) return "No captures available";
        return "Select a raw capture";
    };

    const handleSelectCapture = (evt) => {
        const captureId = evt.target.value;
        if (!captureId) return;
        const capture = rawSurface.captures.find((c) => c.captureId === captureId);
        dispatch(loadCapture({ captureId, fileName: capture?.fileName ?? "" }));
    };

    const handleFileChosen = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setPendingFile(file);
        setDescription("");
        setUploadError(null);
        setUploadOpen(true);
        event.target.value = "";
    };

    const handleUpload = () => {
        const reader = new FileReader();
        reader.onload = async () => {
            const result = await dispatch(uploadCapture({
                auid: rawSurface.selectedAuId,
                fileName: pendingFile.name,
                description,
                contents: reader.result,
            }));
            if (uploadCapture.fulfilled.match(result)) {
                setUploadOpen(false);
                dispatch(loadCaptureList({ auid: rawSurface.selectedAuId }));
            } else {
                setUploadError(result.payload ?? "Upload failed");
            }
        };
        reader.readAsText(pendingFile);
    };

    // Mirrors DataWatcher.js's three-way branch exactly, so both tabs' toolbars show the same
    // loading/error state instead of this one silently rendering a blank AU dropdown while the
    // shared fetch (triggered once, from VisualizerContent.js) is still in flight.
    const renderToolbar = () => (
        <Stack direction="row" spacing={2.5} alignItems="flex-start" flexWrap="wrap">
            <Box sx={{ minWidth: 220 }}>
                <Typography sx={fieldLabelSx}>Alignment Unit</Typography>
                <Select
                    fullWidth
                    size="small"
                    value={rawSurface.selectedAuId}
                    onChange={handleSelectAu}
                    displayEmpty
                    renderValue={(value) => {
                        const au = scans.au.find((a) => a.uniqueId === value);
                        return twoLineValue(au ? au.name : <em>Select an Alignment Unit</em>);
                    }}
                >
                    <MenuItem value=""><em>Select an Alignment Unit</em></MenuItem>
                    {scans.au.map((au, index) => (
                        <MenuItem value={au.uniqueId} key={`rs-au-${index}`}>{au.name}</MenuItem>
                    ))}
                </Select>
            </Box>

            <Box sx={{ minWidth: 300 }}>
                <Typography sx={fieldLabelSx}>Raw Capture</Typography>
                <Select
                    fullWidth
                    size="small"
                    disabled={!rawSurface.selectedAuId}
                    value={rawSurface.captureId ?? ""}
                    onChange={handleSelectCapture}
                    displayEmpty
                    renderValue={(value) => {
                        const capture = rawSurface.captures.find((c) => c.captureId === value);
                        if (capture) {
                            const secondary = `${formatDateTime(capture.uploadedAt)}${capture.description ? ` · ${capture.description}` : ""}`;
                            return twoLineValue(capture.fileName || capture.captureId, secondary);
                        }
                        return twoLineValue(<em>{captureFieldText()}</em>);
                    }}
                >
                    <MenuItem value=""><em>{captureFieldText()}</em></MenuItem>
                    {rawSurface.captures.map((c) => (
                        <MenuItem value={c.captureId} key={c.captureId}>
                            <Stack>
                                <Box>{c.fileName || c.captureId}</Box>
                                <Box sx={{ color: tokens.muted }}>
                                    <Typography noWrap sx={{ fontSize: "12px" }}>
                                        {formatDateTime(c.uploadedAt)}
                                        {c.description ? ` · ${c.description}` : ""}
                                    </Typography>
                                </Box>
                            </Stack>
                        </MenuItem>
                    ))}
                </Select>
            </Box>

            <input
                type="file"
                accept=".json,application/json"
                ref={fileInput}
                onChange={handleFileChosen}
                style={{ display: "none" }}
            />
            <Box>
                {/* Hidden label matching the field labels' height, so this starts at the same
                    row position the dropdowns do, then centers within FIELD_HEIGHT below that -
                    vertically centered against the dropdown itself. Mirrors DataWatcher's
                    Delete Scan / Upload Scan buttons, so every toolbar control sits at the same
                    height across both tabs. */}
                <Typography sx={{ ...fieldLabelSx, visibility: "hidden" }}>Upload</Typography>
                <Box sx={{ height: FIELD_HEIGHT, display: "flex", alignItems: "center" }}>
                    <Button
                        variant="outlined"
                        color="inherit"
                        size="small"
                        disabled={!rawSurface.selectedAuId || busy}
                        onClick={() => fileInput.current?.click()}
                    >
                        Upload capture&hellip;
                    </Button>
                </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Right-justified via the flexGrow spacer above, but height-aligned with Upload
                capture the same way: a hidden label reserves the same top row, then the button
                centers within FIELD_HEIGHT below that. */}
            <Box>
                <Typography sx={{ ...fieldLabelSx, visibility: "hidden" }}>Compare</Typography>
                <Box sx={{ height: FIELD_HEIGHT, display: "flex", alignItems: "center" }}>
                    <Button
                        color="inherit"
                        size="small"
                        disabled={rawSurface.savedScans.length === 0}
                        onClick={() => setHistoryOpen(true)}
                    >
                        Compare Saved Scans&hellip;
                    </Button>
                </Box>
            </Box>
        </Stack>
    );

    return (
        <Box sx={{
            backgroundColor: tokens.surface,
            borderBottom: `1px solid ${tokens.line}`,
            padding: "10px 20px",
        }}>
            {
                (scans.auStatus === "loading" || scans.auStatus === "idle") ? (
                    <>
                        <CircularProgress size={20} />
                        <span>Loading available Alignment Units...</span>
                    </>
                ) : (scans.auStatus === "error") ? (
                    <Alert severity="error">Error loading Alignment Units.</Alert>
                ) : (scans.au.length === 0) ? (
                    <Alert severity="info">No Alignment Units are available...</Alert>
                ) : (
                    renderToolbar()
                )
            }

            <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Upload Raw Capture</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2">
                            File: <strong>{pendingFile?.name}</strong>
                        </Typography>
                        <TextField
                            label="Description"
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder="e.g. coupling recheck"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                        {uploadError && (
                            <Typography variant="body2" color="error">{uploadError}</Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button color="inherit" onClick={() => setUploadOpen(false)} disabled={busy}>Cancel</Button>
                    <Button variant="contained" onClick={handleUpload} disabled={busy}>
                        Upload
                    </Button>
                </DialogActions>
            </Dialog>

            <RawSurfaceHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />
        </Box>
    );
}
